import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';
import { ClientDto, ClientSummaryOverviewDto } from '../../commonTypes/client.types';
import { removeUndefined } from '../../util/general';
import { restoreAll } from '../restoreAll';
import { DateTime } from 'luxon';
import { TimeLogUserSummaryWithUserInfoDto } from '../../commonTypes/acs/timelog.types';

export interface ClientsState {
    value: ClientDto[];
    showCreateClientForm: boolean;
    showEditClientForm: boolean;
    selectedProjectId: string;
    prevMonth: string;
    currentMonth: string;
    selectedSummary: TimeLogUserSummaryWithUserInfoDto | null;
    selectedUnsubmittedSummary: TimeLogUserSummaryWithUserInfoDto | null;
    summary: TimeLogUserSummaryWithUserInfoDto[];
    cachedClientSummaryOverview: Record<string, ClientSummaryOverviewDto[]>;
    clientSummaryOverviewLastFetched: Record<string, number>;
}

const initialState: ClientsState = {
    value: [],
    showCreateClientForm: false,
    showEditClientForm: false,
    selectedProjectId: '',
    prevMonth: DateTime.now().minus({ months: 1 }).toISO(),
    currentMonth: DateTime.now().toISO(),
    selectedSummary: null,
    selectedUnsubmittedSummary: null,
    summary: [],
    cachedClientSummaryOverview: {},
    clientSummaryOverviewLastFetched: {},
};

export const clientsSlice = createSlice({
    name: 'clients',
    initialState,
    extraReducers: builder => builder.addCase(restoreAll, () => initialState),
    reducers: {
        setClients: (state, action) => {
            state.value = action.payload;
        },
        addClient: (state, action) => {
            state.value.push(action.payload);
        },
        setShowCreateClientForm: (state, action) => {
            state.showCreateClientForm = action.payload;
        },
        setShowEditClientForm: (state, action) => {
            state.showEditClientForm = action.payload;
        },
        editClient: (state, action) => {
            const { clientId, updateClient } = action.payload;
            const clientToUpdate = state.value.find(client => clientId === client.id);
            if (!clientToUpdate) {
                console.log('Client which contains contact for updated does not exits');
                return;
            }

            const filteredPayload = removeUndefined(updateClient);
            Object.assign(clientToUpdate, filteredPayload);
        },
        updateClientAddress: (state, action) => {
            const { clientId, addressType, address } = action.payload;
            const clientToUpdate = state.value.find(client => clientId === client.id);
            if (!clientToUpdate) {
                console.log('Client which contains address for updated does not exits');
                return;
            }

            if (addressType === 'billingAddress') {
                clientToUpdate['billingAddress'] = address;
                return;
            }
            clientToUpdate['shippingAddress'] = address;
        },
        updateClientCustomFields: (state, action) => {
            const { clientId, customFields } = action.payload;
            const clientToUpdate = state.value.find(client => clientId === client.id);
            if (!clientToUpdate) {
                console.log('Client which contains customFields for updated does not exits');
                return;
            }

            clientToUpdate.customFields = customFields;
        },
        addClientCustomField: (state, action) => {
            const { clientId, customField } = action.payload;
            const clientToUpdate = state.value.find(client => clientId === client.id);
            if (!clientToUpdate) {
                console.log('Client which contains customFields for updated does not exits');
                return;
            }

            if (clientToUpdate.customFields) {
                clientToUpdate.customFields.push(customField);
                return;
            }

            clientToUpdate.customFields = [customField];
        },
        addContact: (state, action) => {
            const { clientId, contact } = action.payload;

            const client = state.value.find(client => client.id === clientId);
            if (!client) {
                console.log('Client which contains contact for updated does not exits');
                return;
            }

            if (client.contacts) client.contacts.push(contact);
            else client.contacts = [contact];
        },
        editContact: (state, action) => {
            const { clientId, contact } = action.payload;

            const client = state.value.find(client => client.id === clientId);
            if (!client) {
                console.log('Client which contains contact for updated does not exits');
                return;
            }

            const contactToUpdate = client.contacts?.find(existingContact => existingContact.id === contact.id);
            if (!contactToUpdate) {
                console.log(`Contact to update does not exist`);
                return;
            }

            const filteredPayload = removeUndefined(contact);
            Object.assign(contactToUpdate, filteredPayload);
        },
        deleteContact: (state, action) => {
            const { contact } = action.payload;
            const client = state.value.find(client => client.id === contact.clientId);
            if (client && client.contacts && client.contacts.length > 0) {
                const contactToDeleteIdx = client.contacts.findIndex(c => contact.id === c.id);
                if (contactToDeleteIdx === -1) return;
                client.contacts.splice(contactToDeleteIdx, 1);
            }
        },
        deleteClient: (state, action) => {
            state.value = state.value.filter(client => client.id !== action.payload);
        },
        setSelectedProjectId: (state, action) => {
            state.selectedProjectId = action.payload;
        },
        setPrevMonth(state, action: PayloadAction<string>) {
            state.prevMonth = action.payload;
        },
        setCurrentMonth(state, action: PayloadAction<string>) {
            state.currentMonth = action.payload;
        },
        setSelectedSummary(state, action: PayloadAction<TimeLogUserSummaryWithUserInfoDto | null>) {
            state.selectedSummary = action.payload;
        },
        setSummary(state, action: PayloadAction<TimeLogUserSummaryWithUserInfoDto[]>) {
            state.summary = action.payload;
        },
        setUnsubmittedSelectedSummary(state, action: PayloadAction<TimeLogUserSummaryWithUserInfoDto | null>) {
            state.selectedUnsubmittedSummary = action.payload;
        },
        setCachedClientSummaryOverview: (state, action: PayloadAction<{ key: string; data: ClientSummaryOverviewDto[] }>) => {
            state.cachedClientSummaryOverview[action.payload.key] = action.payload.data;
            state.clientSummaryOverviewLastFetched[action.payload.key] = Date.now();
        },
        invalidateClientTimesheetCache: (state, action: PayloadAction<string>) => {
            Object.keys(state.cachedClientSummaryOverview).forEach(key => {
                if (key.startsWith(`${action.payload}-`)) {
                    delete state.cachedClientSummaryOverview[key];
                    delete state.clientSummaryOverviewLastFetched[key];
                }
            });
        },
    },
});

export const {
    setClients,
    editClient,
    addContact,
    editContact,
    deleteContact,
    setShowCreateClientForm,
    setShowEditClientForm,
    deleteClient,
    updateClientAddress,
    addClient,
    updateClientCustomFields,
    addClientCustomField,
    setSelectedProjectId,
    setPrevMonth,
    setCurrentMonth,
    setSelectedSummary,
    setUnsubmittedSelectedSummary,
    setSummary,
    setCachedClientSummaryOverview,
    invalidateClientTimesheetCache,
} = clientsSlice.actions;

export const selectClients = (state: RootState) => state.clients.value;
export const selectShowClientForm = (state: RootState): Record<string, boolean> => ({
    create: state.clients.showCreateClientForm,
    edit: state.clients.showEditClientForm,
});
export const selectSelectedProjectId = (state: RootState) => state.clients.selectedProjectId;
export const selectPrevMonth = (state: any) => state.clients.prevMonth;
export const selectCurrentMonth = (state: any) => state.clients.currentMonth;
export const selectSelectedSummary = (state: RootState) => state.clients.selectedSummary;
export const selectUnsubmittedSelectedSummary = (state: RootState) => state.clients.selectedUnsubmittedSummary;

export const selectCachedClientSummaryOverview = (key: string) => (state: { clients: ClientsState }) =>
    state.clients.cachedClientSummaryOverview[key];
export const selectClientSummaryOverviewLastFetched = (key: string) => (state: { clients: ClientsState }) =>
    state.clients.clientSummaryOverviewLastFetched[key];

export default clientsSlice.reducer;
